import { employeeRangeSchema, type EmployeeRangeValue } from '@houzy/schemas/partner-cockpit/company';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, type ChangeEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

import { onboardingApplicationQueryOptions, useSaveCompanyDataMutation } from '../api/onboardingApi';
import { OnboardingFooter } from '../components/OnboardingFooter';
import { OnboardingStepContent } from '../components/OnboardingStepContent';
import { OnboardingStepError } from '../components/OnboardingStepError';
import { OnboardingStepHeading } from '../components/OnboardingStepHeading';
import { useOnboardingState, type CompanyForm } from '../state/onboardingContext';
import { getNextStep } from '../steps';

const EMPLOYEE_OPTIONS: ReadonlyArray<{ value: EmployeeRangeValue; label: string }> = [
    { value: '1-5', label: '1–5' },
    { value: '6-20', label: '6–20' },
    { value: '21-50', label: '21–50' },
    { value: '51-200', label: '51–200' },
    { value: '201+', label: '201+' },
];

const EMPLOYEE_RANGE_SET: ReadonlySet<string> = new Set(employeeRangeSchema.options);

function isEmployeeRange(value: string): value is EmployeeRangeValue {
    return EMPLOYEE_RANGE_SET.has(value);
}

const companyFormSchema = z.object({
    companyName: z.string().trim().min(1),
    street: z.string().trim().min(1),
    nr: z.string().trim().min(1),
    city: z.string().trim().min(1),
    postcode: z.string().trim().min(1),
    website: z.string().trim(),
    employees: employeeRangeSchema,
});

export function CompanyStep(): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { company, setCompany } = useOnboardingState();
    const { data: application } = useQuery(onboardingApplicationQueryOptions);
    const saveCompanyData = useSaveCompanyDataMutation();

    const update = <K extends keyof CompanyForm>(key: K, value: CompanyForm[K]): void => {
        setCompany((prev) => ({ ...prev, [key]: value }));
    };

    const prefilled = useRef(false);
    const savedCompany = application?.companyData;
    useEffect(() => {
        if (prefilled.current || !savedCompany) {
            return;
        }
        prefilled.current = true;
        setCompany((prev) => {
            if (
                prev.companyName ||
                prev.street ||
                prev.nr ||
                prev.city ||
                prev.postcode ||
                prev.website ||
                prev.employees
            ) {
                return prev;
            }
            return {
                companyName: savedCompany.name,
                street: savedCompany.street,
                nr: savedCompany.nr,
                city: savedCompany.city,
                postcode: savedCompany.postcode,
                website: savedCompany.website ?? '',
                employees: savedCompany.employees,
            };
        });
    }, [savedCompany, setCompany]);

    const handleEmployees = (event: ChangeEvent<HTMLInputElement>): void => {
        const raw = event.target.value;
        update('employees', raw === '' || isEmployeeRange(raw) ? raw : '');
    };

    const parsed = companyFormSchema.safeParse(company);

    const handleNext = (): void => {
        if (!parsed.success) {
            return;
        }
        saveCompanyData.mutate(
            {
                name: parsed.data.companyName,
                street: parsed.data.street,
                nr: parsed.data.nr,
                city: parsed.data.city,
                postcode: parsed.data.postcode,
                employees: parsed.data.employees,
                ...(parsed.data.website ? { website: parsed.data.website } : {}),
            },
            {
                onSuccess: () => {
                    const next = getNextStep('company');
                    if (next) {
                        void navigate({ to: next.path });
                    }
                },
            },
        );
    };

    return (
        <>
            <OnboardingStepContent>
                <Stack spacing={6}>
                    <OnboardingStepHeading
                        title={t('ONBOARDING.COMPANY.TITLE')}
                        description={t('ONBOARDING.COMPANY.DESCRIPTION')}
                    />

                    <OnboardingStepError error={saveCompanyData.error} />

                    <Stack component="form" spacing={2.5} noValidate>
                        <TextField
                            label={t('ONBOARDING.COMPANY.FIELDS.COMPANY_NAME')}
                            fullWidth
                            autoComplete="organization"
                            value={company.companyName}
                            onChange={(e) => update('companyName', e.target.value)}
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label={t('ONBOARDING.COMPANY.FIELDS.ADDRESS')}
                                fullWidth
                                sx={{ flex: { sm: 4 } }}
                                autoComplete="address-line1"
                                value={company.street}
                                onChange={(e) => update('street', e.target.value)}
                            />
                            <TextField
                                label={t('ONBOARDING.COMPANY.FIELDS.ADDRESS_NR')}
                                fullWidth
                                sx={{ flex: { sm: 1 } }}
                                value={company.nr}
                                onChange={(e) => update('nr', e.target.value)}
                            />
                        </Stack>
                        <TextField
                            label={t('ONBOARDING.COMPANY.FIELDS.CITY')}
                            fullWidth
                            autoComplete="address-level2"
                            value={company.city}
                            onChange={(e) => update('city', e.target.value)}
                        />
                        <TextField
                            label={t('ONBOARDING.COMPANY.FIELDS.POSTCODE')}
                            fullWidth
                            autoComplete="postal-code"
                            value={company.postcode}
                            onChange={(e) => update('postcode', e.target.value)}
                        />
                        <TextField
                            label={t('ONBOARDING.COMPANY.FIELDS.WEBSITE')}
                            fullWidth
                            autoComplete="url"
                            value={company.website}
                            onChange={(e) => update('website', e.target.value)}
                        />
                        <TextField
                            select
                            fullWidth
                            id="onboarding-company-employees"
                            label={t('ONBOARDING.COMPANY.FIELDS.EMPLOYEES')}
                            value={company.employees}
                            onChange={handleEmployees}>
                            <MenuItem value="" sx={{ display: 'none' }} />
                            {EMPLOYEE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </Stack>
            </OnboardingStepContent>

            <OnboardingFooter
                currentStep="company"
                nextDisabled={!parsed.success || saveCompanyData.isPending}
                onNext={handleNext}
            />
        </>
    );
}
